import { useState } from 'react';
import { ChevronDown, Shirt, Home, Cpu, Wine, ShoppingBag, Wrench } from 'lucide-react';
import Chip from './Chip';

/**
 * Grouped category selector with an optional meta-section accordion.
 *
 * Usage:
 *   <CategoryGroupBar categories={...} activeCategory={...} onCategoryChange={...} />
 *       → flat horizontal scroll (default — used by News, Investor)
 *
 *   <CategoryGroupBar categories={...} activeCategory={...} onCategoryChange={...} grouped />
 *       → "All" pinned + 6 colour-coded meta-sections (used by Reseller)
 *
 * Grouping is NEVER auto-inferred from length — must be explicit to avoid
 * accidentally slicing pills into reseller-only groups on News/Investor.
 */
const RESELLER_META_GROUPS = [
  { key: 'fashion_luxury', label: 'Fashion & Luxury', Icon: Shirt, accent: '#EC4899',
    members: ['sneakers', 'fashion', 'watches'] },
  { key: 'home_beauty', label: 'Home & Beauty', Icon: Home, accent: '#F59E0B',
    members: ['beauty', 'home', 'wellness', 'baby_kids'] },
  { key: 'tech_media', label: 'Tech & Media', Icon: Cpu, accent: '#3B82F6',
    members: ['electronics', 'books', 'tickets'] },
  { key: 'food_drink', label: 'Food & Drink', Icon: Wine, accent: '#A855F7',
    members: ['spirits', 'food'] },
  { key: 'marketplace_resale', label: 'Marketplace & Resale', Icon: ShoppingBag, accent: '#10B981',
    members: ['ecommerce', 'trading_cards', 'toys', 'collectibles', 'gaming'] },
  { key: 'trade_diy', label: 'Trade & DIY', Icon: Wrench, accent: '#64748B',
    members: ['tools_diy', 'auto_parts'] },
];

export default function CategoryGroupBar({ categories, activeCategory, onCategoryChange, grouped = false }) {
  const [openGroup, setOpenGroup] = useState(null);

  // Flat mode — default. Used by News, Investor.
  if (!grouped) {
    return (
      <div
        data-testid="category-filter"
        className="flex gap-1.5 overflow-x-auto hide-scrollbar"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {categories.map(cat => (
          <Chip
            key={cat.key}
            testId={`category-${cat.key}`}
            active={activeCategory === cat.key}
            label={cat.label}
            onClick={() => onCategoryChange(cat.key)}
          />
        ))}
      </div>
    );
  }

  // Grouped mode — Reseller only.
  const catMap = Object.fromEntries(categories.map(c => [c.key, c]));
  const allCat = catMap.all;
  const activeGroupKey = RESELLER_META_GROUPS.find(g => g.members.includes(activeCategory))?.key;

  return (
    <div data-testid="category-filter-grouped" className="flex flex-col gap-2 min-w-0">
      {allCat && (
        <div className="flex">
          <Chip
            testId="category-all"
            active={activeCategory === 'all'}
            label={allCat.label}
            onClick={() => { onCategoryChange('all'); setOpenGroup(null); }}
          />
        </div>
      )}

      <div
        className="flex gap-1.5 overflow-x-auto hide-scrollbar"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {RESELLER_META_GROUPS.map(group => {
          const isOpen = openGroup === group.key;
          const isActiveParent = activeGroupKey === group.key;
          return (
            <button
              key={group.key}
              data-testid={`category-group-${group.key}`}
              onClick={() => setOpenGroup(isOpen ? null : group.key)}
              className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wide transition-all"
              style={{
                background: isOpen || isActiveParent ? group.accent : 'rgba(255,255,255,0.04)',
                color: isOpen || isActiveParent ? '#fff' : 'rgba(255,255,255,0.72)',
                border: `1px solid ${isOpen || isActiveParent ? group.accent : 'rgba(255,255,255,0.08)'}`,
              }}
            >
              <group.Icon size={12} />
              {group.label}
              <ChevronDown
                size={12}
                style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 160ms' }}
              />
            </button>
          );
        })}
      </div>

      {openGroup && (
        <div
          data-testid={`category-group-${openGroup}-members`}
          className="flex gap-1.5 overflow-x-auto hide-scrollbar pl-1"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {RESELLER_META_GROUPS.find(g => g.key === openGroup)?.members
            .map(memberKey => catMap[memberKey])
            .filter(Boolean)
            .map(cat => (
              <Chip
                key={cat.key}
                testId={`category-${cat.key}`}
                active={activeCategory === cat.key}
                label={cat.label}
                onClick={() => onCategoryChange(cat.key)}
              />
            ))}
        </div>
      )}
    </div>
  );
}
